/**
 * @Description 机会点详情-开店区域概览（所选地址位于开店区域内时）
 */

import { FC, useContext, useEffect, useMemo, useState } from 'react';
import styles from './index.module.less';
import cs from 'classnames';
import Evaluations from './components/Evaluations';
import Basic from './components/Basic';
import V2Title from '@/common/components/Feedback/V2Title';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import { getModelClusterDetail, getModelClusterOverView, getSiteModelList } from '@/common/api/networkplan';
import { Col, Row, Spin } from 'antd';
import AreaDetailDrawer from '@/common/components/business/AreaDetailDrawer';
import { modelClusterSurround } from '@/common/api/siteselectionmap';
import { deepCopy, isNotEmptyAny } from '@lhb/func';
import { useDispatch } from 'react-redux';
import { changeCurModelId } from '@/views/iterate/store';
import PlanSpotFormItem from './components/PlanSpotFormItem';
import PlanSpotDetail from './components/PlanSpotDetail';
import { globalOpeningAreaAlias } from '@/common/utils/ways';
import UserInfoContext from '@/layout/context';
import { matchBusinessAreaLoadingIdentification } from '@/common/components/business/DynamicComponent/customize';
import { useBigdataFn } from '@/common/utils/bigdata';

const AreaOverview: FC<{
  clusterId,
  /** 选址地图模型id */
  modelId,
  className?: string,
  /** true机会点详情 false机会点表单 */
  isDetailPage,
  identification?,
  form?,
  formItemData?,
  curCompValue,
  disabled?,
  setCustomGatheringCategoryForYHT?,
  /** 禁用开店区域点击查看详情 */
  disabledArea,
}> = ({
  clusterId,
  modelId, // 选址地图模型id
  className = '',
  isDetailPage = false, // true机会点详情 false机会点表单
  identification,
  form,
  formItemData = {},
  curCompValue,
  disabled,
  setCustomGatheringCategoryForYHT,
  disabledArea, // 禁用开店区域点击查看详情
}) => {
  const dispatch = useDispatch();
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const [modelList, setModelList] = useState<any[]>([]); // 选址地图模型列表
  const [detail, setDetail] = useState<any>({}); // 详情信息
  const [overViewData, setOverViewData] = useState<any>(); // 概览卡片
  const [loading, setLoading] = useState<boolean>(false); // 加载中
  const [drawerData, setDrawerData] = useState<any>({ // 选址地图详情抽屉
    open: false,
    id: ''
  });

  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息

  const openingAreaTaskAlias = useMemo(() => {
    return globalOpeningAreaAlias(globalAliasItemVOs);
  }, [globalAliasItemVOs]);
  const [isPermission, setIsPermission] = useState<boolean>(false); // 是否有创建集客点权限

  // 获取模型列表
  useEffect(() => {
    getSiteModelList().then((val) => {
      setModelList(val);
    });
  }, []);

  useEffect(() => {
    getClusterInfo(clusterId);
  }, [clusterId]);

  const getClusterInfo = (id) => {
    setLoading(true);
    Promise.all([
      getModelClusterDetail({ id, modelConfigId: modelId }),
      modelClusterSurround({ id, modelConfigId: modelId }),
      getModelClusterOverView({ id, modelConfigId: modelId }),
    ])
      .then(([detailRes, modelClusterDetailRes, overview]) => {
        const _detailRes = deepCopy(detailRes);
        _detailRes.surroundingInfoVO = modelClusterDetailRes;
        setDetail(_detailRes);
        setOverViewData(overview);
        setIsPermission(detailRes?.permissions?.find(item => item.event === 'cluster:planSpotCreate'));
      })
      .catch((error) => {
        console.error('Error fetching details:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClick = () => {
    if (disabledArea) return;
    // 展示商圈详情
    if (modelId) {
      dispatch(changeCurModelId(modelId)); // 使用机会点属性的配置作为选址地图模型id
    } else if (isNotEmptyAny(modelList)) {
      dispatch(changeCurModelId(modelList[0].id)); // 使用模型列表的第一个作为选址地图模型id
    }
    setDrawerData({
      open: true,
      id: clusterId
    });
    bigdataFn('4a82bb36-fee3-4e8d-b112-557d8dbc8f55', '机会点-开店区域点击', '点击了机会点-开店区域点击', { op_block: '', op_event: ``, });
  };

  return (
    <>
      <V2Title
        type='H2'
        divider
        // text='所属开店区域信息'
        text={`所属${openingAreaTaskAlias}信息`}
        className={cs('pb-16', isDetailPage && 'mt-24')}/>
      <Spin spinning={loading}>
        <div className={cs(styles.basCon, !isDetailPage && 'mb-16', className || '', disabledArea && styles.notAllowClick)} onClick={handleClick}>
          <Evaluations
            detail={detail}
            overView={overViewData}
          />
          <Basic
            detail={detail}
            overView={overViewData}
          />
        </div>
      </Spin>

      {/* 表单页且还未完成权限请求，如果此时提交操作，因为集客点表单项还未渲染，会造成集客点必填项检查遗漏。因此这里设置临时检查项，加载中时报错提示阻止提交审批 */}
      {
        loading && !isDetailPage && !!formItemData.required ? <Row gutter={24}>
          <Col span={12}>
            <V2FormInput
              label='集客点信息'
              name={matchBusinessAreaLoadingIdentification}
              placeholder='集客点信息获取中'
              disabled
              rules={[{ required: true, message: '集客点信息获取中，请稍后再试' }]}
            />
          </Col>
        </Row> : <></>
      }
      {isPermission
        ? (isDetailPage
          ? <PlanSpotDetail curCompValue={curCompValue}/>
          : <PlanSpotFormItem
            identification={identification}
            form={form}
            formItemData={formItemData}
            curCompValue={curCompValue}
            clusterId={clusterId}
            disabled={disabled}
            setCustomGatheringCategoryForYHT={setCustomGatheringCategoryForYHT}
          />)
        : <></>
      }
      <AreaDetailDrawer
        drawerData={drawerData}
        setDrawerData={setDrawerData}
        viewChanceDetail={false}
        showCollect={false}
        modelId={modelId}
      />
    </>
  );
};

export default AreaOverview;
